import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";
import { Deal } from "./deal.service";

const checkSalesUserRole = (): {
  isSalesUser: boolean;
  isClientAdmin: boolean;
  userId: number | null;
} => {
  let isSalesUser = false;
  let isClientAdmin = false;
  let userId = null;

  const authResponse = localStorage.getItem("authResponse");

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      const tenantId = localStorage.getItem("selectedTenantId");
      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
        : null;

      isSalesUser = tenantObj?.role_key === "SALESUSER";
      isClientAdmin = tenantObj?.role_key === "CLIENTADMIN";
      userId = auth.user_id || null;
    } catch (e) {
      console.warn("Could not parse auth response to check for user role", e);
    }
  }

  return { isSalesUser, isClientAdmin, userId };
};

export interface KanbanStage {
  lifecycle_type_stage_id: number;
  tenant_id: number;
  lifecycle_type_id: number;
  stage_name: string;
  stage_order: number;
  count: number;
}

export interface KanbanDeal extends Deal {
  deal_id: number;
  title: string;
  lifecycle_source: {
    lifecycle_source_id: number;
    lifecycle_source_key: string;
    lifecycle_source_display_name: string;
  };
  currency_code: string;
  priority: string;
  products_n_services: string;
  company: {
    company_id: number;
    company_name: string;
    company_logo_url: string;
    avatar_url: string;
    address: string;
  };
  lifecycle_type_stage: {
    lifecycle_type_stage_id: number;
    tenant_id: number;
    lifecycle_type_id: number;
    stage_name: string;
    stage_order: number;
  };
}

export interface KanbanDealStage extends KanbanStage {
  deals: KanbanDeal[];
}

export interface KanbanDealResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    stages: KanbanDealStage[];
  };
}

export class DealKanbanService {
  static async fetchKanbanData(
    tenantId: number,
    limit: number = 20,
    page: number = 1,
    filters?: Record<string, any>
  ): Promise<KanbanDealResponse> {
    try {
      const { isSalesUser, userId } = checkSalesUserRole();

      const archivedFilter = filters?.archivedFilter || "no";
      let isArchiveParam = "";
      
      if (archivedFilter === "yes") {
        isArchiveParam = "&is_archive=true";
      } else if (archivedFilter === "no") {
        isArchiveParam = "&is_archive=false";
      }

      let url = `${API_BASE_URL}/lookup/kanban/deal/?tenant_id=${tenantId}&limit=${limit}${isArchiveParam}`;

      if (page > 1) {
        url += `&page=${page}`;
      }

      if (isSalesUser && userId) {
        url += `&sales_user_id=${userId}`;
      }

      if (filters) {
        Object.keys(filters).forEach((key) => {
          if (["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
            return;
          }
          if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
            url += `&${key}=${encodeURIComponent(filters[key])}`;
          }
        });

        if (filters.searchTerm) {
          url += `&title=${encodeURIComponent(filters.searchTerm)}`;
        }
        if (filters.statusFilter) {
          url += `&lifecycle_type_stage_id=${encodeURIComponent(filters.statusFilter)}`;
        }
      }

      const response = await axios.get<KanbanDealResponse>(url, {
        headers: getAuthHeaders(),
      });

      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async updateDealStage(
    dealId: number,
    stageId: number,
    tenantId: number
  ): Promise<void> {
    try {
      const authResponse = localStorage.getItem("authResponse");
      let currentUserId = null;

      if (authResponse) {
        try {
          const auth = JSON.parse(authResponse);
          currentUserId = auth.user_id;
        } catch (e) {
          console.warn("Could not parse auth response for user_id");
        }
      }

      const updatePayload = {
        lifecycle_type_stage_id: stageId,
        tenant_id: tenantId,
        updated_by: currentUserId,
      };

      await axios.patch(
        `${API_BASE_URL}/deal/${dealId}/`,
        updatePayload,
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }
}
